















































































import { mapGetters } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { truncateString } from '@/helpers/formatString';
import formatDate from '@/helpers/formatDate';

import {
  TAutoClicker,
  EAutoclickerStatus,
  TIMEZONES
} from '@/views/CopyStake/CopyStakeAutoclicker/types';
import {
  deleteAutoclickerConfig,
  getAutoclickerGames,
  getAutoclickerList,
  toggleAutoclickerConfig
} from '@/api/CopyStake/AutoClicker';
import { TAutoclickerGameData } from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';

type TAutoclickerState = {
  showEditModal: boolean;
  showConfirmModal: boolean;
  supportedGames: TAutoclickerGameData[];
  list: TAutoClicker[];
  selectedItem: TAutoClicker;
  confirmModalPayload: any;
  loading: Record<string, boolean>;
  tableOptions: { page: number; itemsPerPage: number };
  total: number;
};

const ConfirmationDialog = (): Promise<any> =>
  import('@/components/ConfirmationDialog.vue');
const CopyStakeAutoclickerEditModal = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAutoclicker/AiStreamers/CopyStakeAutoclickerEditModal.vue'
  );

const DEFAULT_PAGINATION_OPTIONS: { page: number; itemsPerPage: number } = {
  page: 1,
  itemsPerPage: 10
};

export default {
  name: 'CopyStakeAutoclickerTab',
  components: {
    CopyStakeAutoclickerEditModal,
    ConfirmationDialog
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data(): TAutoclickerState {
    return {
      showEditModal: false,
      showConfirmModal: false,
      confirmModalPayload: {},
      selectedItem: undefined,
      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS },
      supportedGames: [],
      list: [],
      total: null,
      loading: {
        list: false,
        games: false,
        deleteConfig: false
      }
    };
  },
  watch: {
    active: {
      handler(active: boolean): void {
        if (active) {
          this.getSupportedGames();
        }
      },
      immediate: false
    },
    operatorId: {
      handler(): void {
        if (this.operatorId !== null) {
          this.getSupportedGames();
          this.getAutoclickerList();
        }
      },
      immediate: true
    },
    showEditModal(val: boolean): void {
      if (!val) {
        this.resetSelectedItem();
      }
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    timezonesMap(): Record<string, string> {
      return TIMEZONES.reduce((all, curr) => {
        all[curr.value] = curr.text.split(' ')[0];

        return all;
      }, {});
    },
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    },
    headers(): DataTableHeader[] {
      return [
        { text: 'Nickname', value: 'nickname' },
        { text: 'Game', value: 'games' },
        { text: 'Start Time', value: 'startTime', width: 240 },
        { text: 'End Time', value: 'endTime', width: 240 },
        { text: 'Status', value: 'status' },
        { text: 'Action', value: 'actions', width: 160 }
      ];
    },

    autoclickerList(): any[] {
      return this.list.map((item) => {
        return {
          ...item,
          originalItem: item,
          games: this.formatSupportedGames(item.supportedGameIds),
          startTime: this.convertDate(item.startTime, item.zone),
          endTime: this.convertDate(item.endTime, item.zone),
          active: item.status === EAutoclickerStatus.ACTIVE
        };
      });
    }
  },
  methods: {
    openEditModal(): void {
      this.showEditModal = true;
    },
    convertDate(date: string, zone: string): string {
      return `${formatDate(date, 'yyyy-MM-dd HH:mm', false, false)} ${
        this.timezonesMap[zone]
      }`;
    },
    formatSupportedGames(games: number[]): {
      list: string[];
      suffix?: string;
    } {
      const max = 3;
      const list = games
        .map((gameId) => {
          const game = this.supportedGames.find(({ id }) => id === gameId);

          return game?.gameName;
        })
        .filter(Boolean);
      const total = list.length;

      return {
        list: [...list].slice(0, max),
        suffix: total > max ? `+${total - max} others` : undefined
      };
    },
    async getSupportedGames(): Promise<void> {
      this.loading.games = true;

      getAutoclickerGames()
        .then((data) => {
          this.supportedGames = data;
        })
        .catch(() => {
          this.supportedGames = [];
        })
        .finally(() => {
          this.loading.games = false;
        });
    },
    async getAutoclickerList(): Promise<void> {
      this.loading.list = true;

      getAutoclickerList()
        .then((data) => {
          this.list = data;
        })
        .catch(() => {
          this.list = [];
        })
        .finally(() => {
          this.loading.list = false;
        });
    },
    resetSelectedItem(): void {
      this.selectedItem = undefined;
    },
    editItem(item: TAutoClicker): void {
      this.selectedItem = { ...item };

      this.openEditModal();
    },
    copyItem(item: TAutoClicker): void {
      this.selectedItem = {
        ...item,
        startTime: '',
        endTime: '',
        id: null
      };

      this.openEditModal();
    },
    toggleConfig(item: TAutoClicker): void {
      toggleAutoclickerConfig(item)
        .then(() => {
          if (item.status === EAutoclickerStatus.ACTIVE) {
            this.$toast.success('AutoClicker Config disabled');
          } else {
            this.$toast.success('AutoClicker Config activated');
          }
          this.getAutoclickerList();
        })
        .catch(() => {
          this.$toast.error('AutoClicker Config status not updated');
        });
    },
    deleteConfig(item: TAutoClicker): void {
      this.loading.deleteConfig = true;

      deleteAutoclickerConfig(item)
        .then(() => {
          this.$toast.success(
            `Autoclicker for "${item.nickname}" was successfully deleted`
          );
          this.getAutoclickerList();
          this.showConfirmModal = false;
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.deleteConfig = false;
        });
    },
    handleDeleteConfig(item: TAutoClicker): void {
      this.confirmModalPayload = {
        header: 'Delete Autoclicker Config',
        okText: 'Delete',
        handler: () => this.deleteConfig(item),
        icon: 'mdi-alert-octagon',
        disclaimer: `
          <p>
            Are you sure you want to delete AutoClicker config for nickname <strong>${item.nickname}</strong>?
          </p>
          <p class="text--disabled">You will not be able to recover it.</p>`
      };
      this.showConfirmModal = true;
    },
    truncateString
  }
};
